import { motion } from "framer-motion";
import { ArrowRight } from "lucide-react";
import ContactModal from "../../../../Assets/Common/ContactModal";
import { useState } from "react";

export default function FooterSection() {
  const [openModal, setOpenModal] = useState(false);

  return (
    <>
      {/* ============================================================
          FAQ SECTION
      ============================================================ */}

      <section
        className="
          relative
          overflow-hidden
          py-10
          font-saira
          bg-[var(--bg-primary)]
          text-[var(--text-primary)]

          min-[375px]:py-11

          sm:py-14

          md:py-16

          lg:py-16
        "
      >
        {/* ========================================================
            BACKGROUND GLOW
            GREEN — UNCHANGED IN BOTH THEMES
        ======================================================== */}

        <div
          className="
            pointer-events-none
            absolute
            left-[-100px]
            top-20
            h-48
            w-48
            rounded-full
            bg-green-500/5
            blur-[110px]

            sm:h-60
            sm:w-60
            sm:blur-[130px]

            lg:left-0
            lg:h-80
            lg:w-80
            lg:bg-green-500/10
            lg:blur-[170px]
          "
        />

        <div
          className="
            pointer-events-none
            absolute
            right-[-100px]
            bottom-0
            h-48
            w-48
            rounded-full
            bg-green-500/5
            blur-[110px]

            sm:h-60
            sm:w-60
            sm:blur-[130px]

            lg:right-0
            lg:h-80
            lg:w-80
            lg:bg-green-500/10
            lg:blur-[170px]
          "
        />

        {/* ========================================================
            MAIN CONTAINER
        ======================================================== */}

        <div
          className="
            relative
            z-10
            mx-auto
            w-full
            max-w-7xl
            px-4

            min-[375px]:px-5

            sm:px-6

            md:px-7

            lg:px-8
          "
        >
          {/* ======================================================
              FAQ HEADING
          ====================================================== */}

          <motion.div
            initial={{
              opacity: 0,
              y: 40,
            }}
            whileInView={{
              opacity: 1,
              y: 0,
            }}
            viewport={{
              once: true,
            }}
            className="
              mx-auto
              max-w-3xl
              text-center
            "
          >
            {/* Section Label */}

            <span
              className="
                inline-block
                text-[10px]
                font-semibold
                uppercase
                tracking-[2.5px]
                text-green-400

                min-[375px]:text-[11px]
                min-[375px]:tracking-[3px]

                sm:text-xs
                sm:tracking-[3.5px]

                md:text-sm
                md:tracking-[4px]
              "
            >
              Frequently Asked Questions
            </span>

            {/* Heading */}

            <h2
              className="
                mt-3
                text-[28px]
                font-bold
                leading-[1.15]
                text-[var(--text-primary)]

                min-[375px]:mt-4
                min-[375px]:text-[30px]

                sm:mt-5
                sm:text-4xl

                lg:text-5xl
              "
            >
              Everything You Need to Know
            </h2>

            {/* Description */}

            <p
              className="
                mx-auto
                mt-3
                max-w-2xl
                text-[13px]
                leading-6
                text-[var(--text-muted)]

                min-[375px]:mt-4
                min-[375px]:text-sm
                min-[375px]:leading-6

                sm:mt-5
                sm:text-base
                sm:leading-7

                lg:mt-6
                lg:leading-8
              "
            >
              Find answers to the most common questions about our WhatsApp
              Business API, integrations, automation, pricing, and platform
              capabilities.
            </p>
          </motion.div>

          {/* ======================================================
              FAQ LIST
          ====================================================== */}

          <div
            className="
              mx-auto
              mt-7
              w-full
              max-w-7xl
              space-y-3

              min-[375px]:mt-8
              min-[375px]:space-y-4

              sm:mt-10
              sm:space-y-5

              lg:space-y-6
            "
          >
            {[
              {
                question: "Is this the official WhatsApp Business API?",
                answer:
                  "Yes. We implement the official WhatsApp Business Platform, ensuring secure, scalable, and policy-compliant business messaging.",
              },
              {
                question: "Can I integrate it with my CRM?",
                answer:
                  "Absolutely. Our APIs can integrate with CRMs, ERPs, eCommerce platforms, and custom business applications.",
              },
              {
                question: "Do you provide chatbot automation?",
                answer:
                  "Yes. We develop intelligent chatbot workflows, automated replies, lead qualification, and customer support automation.",
              },
              {
                question:
                  "Can multiple team members use the same WhatsApp number?",
                answer:
                  "Yes. The platform supports multi-agent collaboration with role-based access, chat assignment, and internal notes.",
              },
            ].map((faq, index) => (
              <motion.details
                key={index}
                initial={{
                  opacity: 0,
                  y: 25,
                }}
                whileInView={{
                  opacity: 1,
                  y: 0,
                }}
                viewport={{
                  once: true,
                }}
                transition={{
                  delay: index * 0.1,
                }}
                className="
                  group
                  overflow-hidden
                  rounded-xl
                  border
                  border-[var(--border-primary)]
                  bg-[var(--card-bg)]
                  p-4
                  transition-all
                  duration-300
                  hover:border-green-500/20

                  min-[375px]:rounded-2xl
                  min-[375px]:p-5

                  sm:p-6

                  lg:rounded-2xl
                "
              >
                {/* ==================================================
                    FAQ QUESTION
                ================================================== */}

                <summary
                  className="
                    flex
                    cursor-pointer
                    list-none
                    items-start
                    gap-3
                    text-[14px]
                    font-semibold
                    leading-6
                    text-[var(--text-primary)]
                    marker:hidden

                    min-[375px]:text-[15px]

                    sm:items-center
                    sm:text-base
                    sm:leading-6

                    md:text-lg
                  "
                >
                  {/* Question */}

                  <span className="min-w-0 flex-1">
                    {faq.question}
                  </span>

                  {/* Plus Icon */}

                  <span
                    className="
                      mt-0.5
                      flex
                      h-6
                      w-6
                      shrink-0
                      items-center
                      justify-center
                      rounded-full
                      text-xl
                      font-normal
                      leading-none
                      text-green-400
                      transition-transform
                      duration-300
                      group-open:rotate-45

                      sm:h-7
                      sm:w-7
                      sm:text-2xl
                    "
                  >
                    +
                  </span>
                </summary>

                {/* ==================================================
                    FAQ ANSWER
                ================================================== */}

                <p
                  className="
                    mt-3
                    max-w-5xl
                    text-[12px]
                    leading-6
                    text-[var(--text-muted)]

                    min-[375px]:mt-4
                    min-[375px]:text-[13px]
                    min-[375px]:leading-6

                    sm:mt-5
                    sm:text-sm
                    sm:leading-7

                    lg:text-base
                    lg:leading-8
                  "
                >
                  {faq.answer}
                </p>
              </motion.details>
            ))}
          </div>
        </div>
      </section>

      {/* ============================================================
          CTA SECTION
      ============================================================ */}

      <section
        className="
          relative
          overflow-hidden
          py-8
          font-saira
          bg-[var(--bg-primary)]
          text-[var(--text-primary)]

          min-[375px]:py-9

          sm:py-10

          md:py-12

          lg:py-10
        "
      >
        {/* ========================================================
            BACKGROUND GLOWS
            GREEN — UNCHANGED IN BOTH THEMES
        ======================================================== */}

        <div
          className="
            pointer-events-none
            absolute
            left-[-100px]
            top-0
            h-52
            w-52
            rounded-full
            bg-green-500/5
            blur-[110px]

            sm:h-64
            sm:w-64
            sm:blur-[125px]

            lg:left-0
            lg:h-72
            lg:w-72
            lg:bg-green-500/10
            lg:blur-[140px]
          "
        />

        <div
          className="
            pointer-events-none
            absolute
            right-[-100px]
            bottom-0
            h-52
            w-52
            rounded-full
            bg-green-500/5
            blur-[110px]

            sm:h-64
            sm:w-64
            sm:blur-[125px]

            lg:right-0
            lg:h-72
            lg:w-72
            lg:bg-green-500/10
            lg:blur-[140px]
          "
        />

        {/* ========================================================
            CTA CONTAINER
        ======================================================== */}

        <div
          className="
            relative
            z-10
            mx-auto
            w-full
            max-w-7xl
            px-4

            min-[375px]:px-5

            sm:px-6

            md:px-7

            lg:px-8
          "
        >
          <motion.div
            initial={{
              opacity: 0,
              y: 40,
            }}
            whileInView={{
              opacity: 1,
              y: 0,
            }}
            viewport={{
              once: true,
            }}
            transition={{
              duration: 0.7,
            }}
            className="
              relative
              overflow-hidden
              rounded-2xl
              border
              border-[var(--border-primary)]
              bg-[var(--card-bg)]
              px-4
              py-8
              text-center

              min-[375px]:rounded-[20px]
              min-[375px]:px-5
              min-[375px]:py-9

              sm:rounded-3xl
              sm:px-8
              sm:py-11

              md:px-10
              md:py-12

              lg:px-16
              lg:py-14
            "
          >
            {/* ==================================================
                CTA INNER GLOW
            ================================================== */}

            <div
              className="
                pointer-events-none
                absolute
                left-1/2
                top-0
                h-32
                w-56
                -translate-x-1/2
                rounded-full
                bg-green-500/5
                blur-[70px]

                sm:h-40
                sm:w-72

                lg:h-48
                lg:w-96
              "
            />

            {/* ==================================================
                CTA CONTENT
            ================================================== */}

            <div className="relative z-10">
              {/* Badge */}

              <span
                className="
                  inline-flex
                  items-center
                  justify-center
                  rounded-full
                  border
                  border-green-500/20
                  bg-green-500/10
                  px-3
                  py-1.5
                  text-[11px]
                  font-medium
                  text-green-400

                  min-[375px]:px-4
                  min-[375px]:py-2
                  min-[375px]:text-xs

                  sm:text-sm
                "
              >
                Ready to Get Started?
              </span>

              {/* Heading */}

              <h2
                className="
                  mx-auto
                  mt-4
                  max-w-4xl
                  text-[27px]
                  font-bold
                  leading-[1.15]
                  text-[var(--text-primary)]

                  min-[375px]:mt-5
                  min-[375px]:text-[29px]

                  sm:mt-6
                  sm:text-3xl
                  sm:leading-tight

                  md:text-4xl

                  lg:text-5xl
                "
              >
                Let's Build Your
                <br />
                <span className="text-green-400">
                  WhatsApp Communication Platform
                </span>
              </h2>

              {/* Description */}

              <p
                className="
                  mx-auto
                  mt-4
                  max-w-2xl
                  text-[12px]
                  leading-6
                  text-[var(--text-muted)]

                  min-[375px]:mt-5
                  min-[375px]:text-[13px]

                  sm:text-sm
                  sm:leading-7

                  md:text-base

                  lg:mt-5
                  lg:leading-7
                "
              >
                Automate conversations, engage customers, and scale your
                business with our secure WhatsApp Business API solutions.
              </p>

              {/* ==================================================
                  BUTTON
                  GREEN — UNCHANGED
              ================================================== */}

              <div
                className="
                  mt-6
                  flex
                  w-full
                  justify-center

                  min-[375px]:mt-7

                  sm:mt-8
                "
              >
                <motion.button
                  whileHover={{
                    y: -2,
                    scale: 1.01,
                  }}
                  whileTap={{
                    scale: 0.97,
                  }}
                  transition={{
                    duration: 0.2,
                  }}
                  onClick={() => {
                    setOpenModal(true);
                  }}
                  className="
                    group
                    flex
                    w-full
                    items-center
                    justify-center
                    gap-2
                    rounded-xl
                    bg-green-500
                    px-5
                    py-3
                    text-[13px]
                    font-semibold
                    text-black
                    transition-all
                    duration-300
                    hover:bg-green-600
                    hover:shadow-[0_15px_40px_rgba(34,197,94,0.25)]

                    min-[375px]:px-6
                    min-[375px]:py-3.5
                    min-[375px]:text-sm

                    sm:w-auto
                    sm:px-7
                    sm:py-3.5
                    sm:text-base
                  "
                >
                  <span>Book a Free Consultation</span>

                  <ArrowRight
                    size={18}
                    className="
                      shrink-0
                      transition-transform
                      duration-300
                      group-hover:translate-x-1
                    "
                  />
                </motion.button>
              </div>
            </div>
          </motion.div>
        </div>

        {/* ========================================================
            CONTACT MODAL
        ======================================================== */}

        <ContactModal
          isOpen={openModal}
          onClose={() => setOpenModal(false)}
          initialService="API Services"
          initialSubService="WhatsApp API Services"
        />
      </section>
    </>
  );
}